// Shared helpers for rendering posts in the feed card, the sidebar row and the
// post page, so the three stay consistent.

const ABSOLUTE_AFTER_DAYS = 7;

/**
 * "2h ago" for anything inside the last week, falling back to the absolute
 * format the feed has always used beyond that.
 */
export const relativeTime = (dateTime) => {
  const then = new Date(dateTime);
  if (isNaN(then)) return "";

  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;

  const days = Math.floor(seconds / 86400);
  if (days < ABSOLUTE_AFTER_DAYS) return `${days}d ago`;

  return then.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * The three stages a report passes through, in order. `statusMeta().step`
 * indexes into this.
 *
 * The first is "Reported" rather than "Pending", because a completed first step
 * means the report was filed, which is true the moment the post exists. The
 * status named "Pending" is the state of *waiting* that follows it.
 */
export const STATUS_STEPS = ["Reported", "In review", "Resolved"];

/**
 * Maps a post's server-computed status onto a label, an icon, a step index and
 * a sentence saying what the status means for the reader. Returns null for
 * posts with no status so callers can skip rendering.
 *
 * `meaning` describes the pipeline as it actually works, so it has to be kept
 * true if that changes. The server derives these three public values from the
 * post's Ticket (server/routes/posts.js): no ticket at all is "Pending action",
 * a ticket whose last status is Resolved is "Resolved", and a ticket in any
 * other state (Under review, Escalated, Awaiting feedback) is "Ongoing action".
 * That is why Pending means nobody has picked it up rather than nobody has
 * looked at it, and why Resolved is the reason the comment box disappears.
 */
export const statusMeta = (status) => {
  switch (status) {
    case "Pending action":
      return {
        key: "pending",
        label: "Pending",
        icon: "far fa-clock",
        step: 0,
        meaning: "Submitted and waiting for our team to pick it up.",
      };
    case "Ongoing action":
      return {
        key: "ongoing",
        label: "In review",
        icon: "fas fa-hourglass-half",
        step: 1,
        meaning: "Our team is investigating this report.",
      };
    case "Resolved":
      return {
        key: "resolved",
        label: "Resolved",
        icon: "far fa-check-circle",
        step: 2,
        meaning:
          "Our team has finished with this report. Comments are closed.",
      };
    default:
      return null;
  }
};
